<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import AdsConversationReferralCard from './AdsConversationReferralCard.vue';
import AdsAPI from '../api';
import { useRastroEnabled } from '../useRastro';

const mountTarget = ref(null);
let hiddenReferralBubble = null;
const hiddenMessageCards = new Set();
let scheduledFrame;
let messageObserver;
let observedPanel;
const isRastroEnabled = useRastroEnabled();
const storedThumbnail = ref('');

const selectedConversation = useMapGetter('getSelectedChat');
const conversationMetadata = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);

const additionalAttributes = computed(() => {
  const conversation = selectedConversation.value || {};
  const metadata = conversation.id
    ? conversationMetadata.value(conversation.id)
    : {};

  return (
    metadata?.additional_attributes || conversation.additional_attributes || {}
  );
});

const referral = computed(() => additionalAttributes.value.referral || null);
const isAdReferral = computed(
  () => referral.value?.source_type !== 'post' && !!referral.value?.source_url
);

const restoreNativeReferralBubble = () => {
  if (hiddenReferralBubble?.isConnected) hiddenReferralBubble.hidden = false;
  hiddenReferralBubble = null;
  hiddenMessageCards.forEach(card => {
    if (card.isConnected) {
      card.hidden = false;
      card.classList.remove('!hidden');
    }
  });
  hiddenMessageCards.clear();
  mountTarget.value?.remove();
  mountTarget.value = null;
};

const hideDuplicateMessageCards = () => {
  const sourceUrl = referral.value?.source_url;
  if (!sourceUrl) return;

  const cards = document.querySelectorAll(
    '.conversation-panel [data-testid="whatsapp-referral"], .conversation-panel [data-bubble-name="text"] > a[href]'
  );
  cards.forEach(card => {
    const link = card.matches('a') ? card : card.querySelector('a[href]');
    if (link?.getAttribute('href') !== sourceUrl) return;
    if (card.matches('a') && !card.querySelector('.i-lucide-megaphone')) return;

    card.hidden = true;
    card.classList.add('!hidden');
    hiddenMessageCards.add(card);
  });
};

const nativeReferralBubble = () => {
  const sourceUrl = referral.value?.source_url;
  if (!sourceUrl) return null;

  return [
    ...document.querySelectorAll(
      '.conversation-panel li[data-clarity-mask="True"]'
    ),
  ].find(item => {
    const hasNativeIcon = item.querySelector('.i-lucide-megaphone');
    const linksToReferral = [...item.querySelectorAll('a[href]')].some(
      link => link.getAttribute('href') === sourceUrl
    );

    return hasNativeIcon && linksToReferral;
  });
};

const ensureMountTarget = () => {
  const panel = document.querySelector('.conversation-panel');
  if (panel && panel !== observedPanel) {
    messageObserver?.disconnect();
    observedPanel = panel;
    messageObserver = new MutationObserver(() => {
      window.cancelAnimationFrame(scheduledFrame);
      scheduledFrame = window.requestAnimationFrame(ensureMountTarget);
    });
    messageObserver.observe(panel, { childList: true, subtree: true });
  }

  if (!isRastroEnabled.value) {
    restoreNativeReferralBubble();
    return;
  }
  if (!isAdReferral.value) {
    restoreNativeReferralBubble();
    return;
  }

  const nativeBubble = nativeReferralBubble();
  if (!nativeBubble) return;
  if (hiddenReferralBubble === nativeBubble && mountTarget.value?.isConnected) {
    hideDuplicateMessageCards();
    return;
  }

  restoreNativeReferralBubble();
  const target = document.createElement('div');
  target.dataset.adsReferralTarget = '';
  nativeBubble.before(target);
  nativeBubble.hidden = true;
  hiddenReferralBubble = nativeBubble;
  mountTarget.value = target;
  hideDuplicateMessageCards();
};

const scheduleMountTarget = () => {
  window.cancelAnimationFrame(scheduledFrame);
  scheduledFrame = window.requestAnimationFrame(ensureMountTarget);
};

const refreshMountTarget = async () => {
  restoreNativeReferralBubble();
  await nextTick();
  scheduleMountTarget();
};

watch(
  [isRastroEnabled, () => selectedConversation.value?.id, referral],
  refreshMountTarget
);

watch(
  [isRastroEnabled, () => selectedConversation.value?.id, referral],
  async ([enabled, conversationId, currentReferral], _oldValue, onCleanup) => {
    storedThumbnail.value = '';
    if (!enabled || !conversationId || !currentReferral) return;

    let active = true;
    onCleanup(() => {
      active = false;
    });
    try {
      const { data } = await AdsAPI.conversationCreative(conversationId);
      if (active) storedThumbnail.value = data.thumbnail_url || '';
    } catch {
      // The original referral remains visible when a stored image is unavailable.
    }
  },
  { immediate: true }
);

onMounted(refreshMountTarget);

onBeforeUnmount(() => {
  window.cancelAnimationFrame(scheduledFrame);
  messageObserver?.disconnect();
  restoreNativeReferralBubble();
});
</script>

<template>
  <Teleport v-if="isRastroEnabled" :to="mountTarget || 'body'">
    <AdsConversationReferralCard
      v-if="mountTarget && isAdReferral"
      :referral="referral"
      :thumbnail-url="storedThumbnail"
    />
  </Teleport>
</template>
